import { DiscordSDK } from "@discord/embedded-app-sdk";
import { ActivityBootstrapSchema } from "./bootstrap-schema";
import { createDemoBootstrap, type ActivityBootstrap } from "@tribe/domain";

const configuredApiUrl = import.meta.env.VITE_API_URL as string | undefined;
const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;

/**
 * Where the Worker lives, from the running page's point of view.
 *
 * Inside Discord the Activity is served from `<client_id>.discordsays.com` under a content
 * security policy that permits that origin and nothing else, so the absolute Worker URL is not
 * reachable from the iframe at all. Everything has to travel through Discord's proxy, which
 * forwards `/.proxy/<prefix>/...` to whatever host the Activity URL Mapping names.
 *
 * That leaves two shapes for the same base, and the difference is invisible in a browser
 * preview — which is exactly how an Activity ships looking healthy and fails the moment it is
 * opened in the client. `null` means the demo build, which talks to nobody.
 */
let apiBase: string | null = configuredApiUrl ?? null;

/**
 * The Activity session, held in memory for the life of the page.
 *
 * It arrives from `/api/token` and rides on every later request as a bearer header. The Worker
 * also sets it as a cookie, but a cookie has to survive the proxy hop and third-party context to
 * be worth anything, and this one does not have to.
 */
let sessionToken: string | null = null;
let presenceSocket: WebSocket | null = null;
let discordSdk: DiscordSDK | null = null;
let discordLocation: { guildId?: string; channelId?: string } = {};

export interface ActivityContext {
  mode: "discord" | "demo";
  guildId?: string;
  channelId?: string;
}

/** The headers every authenticated call shares. */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    "content-type": "application/json",
    ...(sessionToken ? { authorization: `Bearer ${sessionToken}` } : {}),
    ...extra
  };
}

export async function initializeActivity(options?: { onThermalState?: (state: number) => void; onLayoutMode?: (mode: number) => void }): Promise<ActivityContext> {
  if (!clientId) return { mode: "demo" };
  const sdk = new DiscordSDK(clientId);
  discordSdk = sdk;
  await sdk.ready();
  // Everything from here on is proxied. Set the base before the first request rather than after,
  // because `/api/token` is itself one of the calls that has to go through it.
  apiBase = "/.proxy/api";
  await sdk.commands.setOrientationLockState({ lock_state: 2, picture_in_picture_lock_state: 2, grid_lock_state: 2 }).catch(() => undefined);
  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: "code",
    state: "",
    prompt: "none",
    // `identify` names the member and `guilds` confirms they belong here. The Activity issues no
    // commands of its own, so asking for `applications.commands` would only add a consent line.
    scope: ["identify", "guilds"]
  });
  if (!sdk.guildId) throw new Error("The private Activity requires a guild context");
  const tokenResponse = await fetch(`${apiBase}/api/token`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code, guildId: sdk.guildId })
  });
  if (!tokenResponse.ok) throw new Error("Discord authorization could not be completed");
  const { access_token, session_token } = await tokenResponse.json() as { access_token: string; session_token?: string };
  sessionToken = session_token ?? null;
  const auth = await sdk.commands.authenticate({ access_token });
  if (!auth) throw new Error("Discord authentication did not finish");
  discordLocation = { ...(sdk.guildId ? { guildId: sdk.guildId } : {}), ...(sdk.channelId ? { channelId: sdk.channelId } : {}) };
  await sdk.subscribe("THERMAL_STATE_UPDATE", ({ thermal_state }) => options?.onThermalState?.(thermal_state)).catch(() => undefined);
  await sdk.subscribe("ACTIVITY_LAYOUT_MODE_UPDATE", ({ layout_mode }) => options?.onLayoutMode?.(layout_mode)).catch(() => undefined);
  return {
    mode: "discord",
    ...(sdk.guildId ? { guildId: sdk.guildId } : {}),
    ...(sdk.channelId ? { channelId: sdk.channelId } : {})
  };
}

export async function loadBootstrap(): Promise<ActivityBootstrap> {
  if (!apiBase) return createDemoBootstrap();
  const response = await fetch(`${apiBase}/api/v1/bootstrap`, { credentials: "include", headers: authHeaders() });
  if (!response.ok) throw new Error(`Village data could not be loaded (${response.status})`);
  return ActivityBootstrapSchema.parse(await response.json()) as ActivityBootstrap;
}

/**
 * @param method The route's verb. Most endpoints are POST; the property save is a PATCH, and
 * sending it as a POST meant every placement, pet and plant save 404'd against the real worker.
 * Only the demo build hid it, by short-circuiting before the request went out.
 */
export async function apiMutation<T>(
  path: string,
  body: unknown,
  method: "POST" | "PATCH" | "DELETE" = "POST"
): Promise<T | undefined> {
  if (!apiBase) return undefined;
  const response = await fetch(`${apiBase}${path}`, {
    method,
    credentials: "include",
    headers: authHeaders({ "x-idempotency-key": crypto.randomUUID() }),
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "The village could not save that change" })) as { message?: string };
    throw new Error(error.message ?? `Request failed (${response.status})`);
  }
  return await response.json() as T;
}

/** A plain read. Separate from `apiMutation` because a GET carries no body and no idempotency key. */
export async function apiQuery<T>(path: string): Promise<T | undefined> {
  if (!apiBase) return undefined;
  const response = await fetch(`${apiBase}${path}`, { credentials: "include", headers: authHeaders() });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "The village could not load that" })) as { message?: string };
    throw new Error(error.message ?? `Request failed (${response.status})`);
  }
  return await response.json() as T;
}

/** Whether this build is talking to a real Worker at all. */
export function isLive() {
  return apiBase !== null;
}

export function connectVillagePresence(onMessage: (message: unknown) => void) {
  if (!apiBase) return () => undefined;
  // Relative under the proxy, absolute in the browser preview. `new URL` needs a base for the
  // first case, and the page's own origin is the right one — that is where the proxy lives.
  const url = new URL(`${apiBase}/api/v1/realtime`, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // The proxy cannot carry an Authorization header on a WebSocket handshake, so the session
  // travels as a query parameter on this one route. The Worker accepts it there and nowhere else.
  if (sessionToken) url.searchParams.set("session", sessionToken);
  presenceSocket = new WebSocket(url);
  presenceSocket.addEventListener("message", (event) => {
    try { onMessage(JSON.parse(String(event.data))); } catch { /* malformed presence messages are ignored */ }
  });
  return () => { presenceSocket?.close(1000, "Activity closed"); presenceSocket = null; };
}

export function sendPresence(message: { type: "move" | "wave" | "react"; x?: number; z?: number; reaction?: string }) {
  if (presenceSocket?.readyState === WebSocket.OPEN) presenceSocket.send(JSON.stringify(message));
}

/** The account export, as a file the member can keep. Returns false in the demo build. */
export async function downloadAccountExport(): Promise<boolean> {
  if (!apiBase) return false;
  const response = await fetch(`${apiBase}/api/v1/account/export`, { credentials: "include", headers: authHeaders() });
  if (!response.ok) throw new Error("That export could not be prepared");
  const blob = new Blob([JSON.stringify(await response.json(), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `tiny-tribe-village-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
  return true;
}

/** Permanent. The building geometry stays in the village; the identity behind it does not. */
export async function deleteAccount(): Promise<boolean> {
  if (!apiBase) return false;
  const response = await fetch(`${apiBase}/api/v1/account`, { method: "DELETE", credentials: "include", headers: authHeaders() });
  if (!response.ok) throw new Error("That account could not be removed");
  sessionToken = null;
  return true;
}

export async function returnToDiscord() {
  if (!discordSdk || !discordLocation.guildId || !discordLocation.channelId) return;
  await discordSdk.commands.openExternalLink({ url: `https://discord.com/channels/${discordLocation.guildId}/${discordLocation.channelId}` }).catch(() => undefined);
}
